import { ExternalLink, Ship } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { FerryCrossing, UiLang } from "../lib/types";
import { StaleNote } from "./StaleNote";

interface Props {
  crossings: FerryCrossing[];
  /** Tidspunktet planen ble lagret, når den er hentet fra lagringen. */
  staleSince?: number | null;
  lang: UiLang;
}

/**
 * Ferjeavgangene på strekningene ruta må over.
 *
 * Bodø–Moskenes er tre og en halv time og går fire ganger i døgnet. Ferja inn
 * Lysefjorden går bare i sesong. Det er ikke en detalj man slår opp etterpå —
 * det er den ene tiden på dagen resten av planen må legges rundt.
 *
 * Dette er ruteplanen, ikke skipets posisjon. Live AIS krever konto hos
 * BarentsWatch, og appen har ingen nøkler. Ruteplanen med avlysninger er
 * dessuten det man planlegger etter; posisjonen er det man ser fra kaia.
 */
export function FerryPanel({ crossings, staleSince, lang }: Props) {
  const t = dictionaries[lang];
  if (crossings.length === 0) return null;

  return (
    <section className="card ferry">
      <div className="card-title">
        <Ship size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
        {t.ferryTitle}
      </div>
      <p className="card-sub">{t.ferrySubtitle}</p>
      {staleSince ? <StaleNote savedAt={staleSince} lang={lang} /> : null}

      {crossings.map((crossing) => (
        <div className="ferry-crossing" key={`${crossing.stopId}-${crossing.toName}`}>
          <h3 className="ferry-route">
            {crossing.quayName} <span className="ferry-arrow">→</span> {crossing.toName}
          </h3>

          <ul className="ferry-departures">
            {crossing.departures.map((departure) => (
              <li
                className={`ferry-departure ${departure.cancelled ? "ferry-departure--off" : ""}`}
                key={departure.departure}
              >
                <span className="ferry-time">
                  {clock(departure.departure, lang)}
                  <span className="ferry-arrow">→</span>
                  {clock(departure.arrival, lang)}
                </span>
                <span className="ferry-duration">{t.ferryDuration(departure.durationMinutes)}</span>
                {departure.cancelled && <span className="ferry-cancelled">{t.ferryCancelled}</span>}
                {/* «C-rute» betyr noe for den som kjenner ruta, og ingenting for oss å tolke. */}
                {departure.notices.length > 0 && (
                  <span className="ferry-notice">{departure.notices.join(" · ")}</span>
                )}
              </li>
            ))}
          </ul>

          {crossing.departures[0]?.operator && (
            <p className="ferry-operator">
              {crossing.departures[0].operatorUrl ? (
                <a href={crossing.departures[0].operatorUrl} target="_blank" rel="noreferrer">
                  {crossing.departures[0].operator} <ExternalLink size={10} />
                </a>
              ) : (
                crossing.departures[0].operator
              )}
            </p>
          )}
        </div>
      ))}

      <p className="traffic-credit">{t.ferryCredit}</p>
    </section>
  );
}

/** Klokkeslettet, i den reisendes egen tidssone-uavhengige lesning. */
function clock(iso: string, lang: UiLang): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString(lang === "no" ? "nb-NO" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
